import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axiosInstance from 'Services/APIService';
import { Invoice } from 'types/ndisSection/invoiceTypes';
import { Organization } from '../carporateServices/organizationSlice';

// Define Client interface based on API response
export interface Client {
  client_name: string;
  email: string;
  ndis_number: string;
  phone: string;
  address: string;
  emergency_contact_name: string;
  emergency_contact_phone: string;
  avatar: string;
  manager_email?: string;
}

interface InvoicePayload {
  user_whoRequested_id: string;
  group_id: string;
  service_name_id: string;
  nested_component_id: string;
  client_name: string | string[];
  filter_date: string;
  client_key?: string;
}

interface ClientAndOrgPayload {
  group_id: string;
  service_name_id: string;
  client_name: string;
}

interface SendInvoicePayload {
  email: string;
  pdf_base64: string;
  filename: string;
  client_name: string;
  invoice_number: string;
  plan_manager_name: string;
  group_id: string;
  service_id: string;
}

interface InvoiceState {
  invoices: Invoice[];
  generatedInvoices: Invoice[];
  current_invoice: Invoice | null;
  organization: Organization | null;
  client: Client | null;
  loading: boolean;
  error: string | null;
  sending: boolean;
  sendError: string | null;
}

const initialState: InvoiceState = {
  invoices: [],
  generatedInvoices: [],
  current_invoice: null,
  organization: null,
  client: null,
  loading: false,
  error: null,
  sending: false,
  sendError: null
};

export const fetchInvoice = createAsyncThunk(
  'invoice/fetchInvoice',
  async (payload: InvoicePayload, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post('/get/invoiceList', payload);
      console.log('fetchInvoice response:', response.data);
      let invoices: Invoice[] = [];
      if (Array.isArray(response.data)) {
        invoices = response.data as Invoice[];
      } else if (typeof response.data === 'object') {
        invoices = (Object.values(response.data) as Invoice[][]).flat();
      }
      return invoices;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch invoices');
    }
  }
);

export const generateInvoice = createAsyncThunk(
  'invoice/generateInvoice',
  async (
    payload: InvoicePayload & { supports_delivered_from: string; supports_delivered_to: string },
    { rejectWithValue }
  ) => {
    try {
      const response = await axiosInstance.post('/generate/invoiceData', payload);
      console.log('generateInvoice response:', response.data);
      let invoices: Invoice[] = [];
      if (Array.isArray(response.data)) {
        invoices = response.data as Invoice[];
      } else if (typeof response.data === 'object') {
        invoices = (Object.values(response.data) as Invoice[][]).flat();
      }
      console.log('rrrrrrrrrrrrrr', invoices);
      return invoices;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to generate invoices');
    }
  }
);

export const fetchSingleInvoice = createAsyncThunk(
  'invoice/fetchSingleInvoice',
  async (payload: InvoicePayload & { invoice_id: string }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post('/get/invoiceData', payload);
      console.log('fetchSingleInvoice response:', response.data);

      let invoice: Invoice | undefined;

      if (Array.isArray(payload.client_name)) {
        invoice = response.data[payload.client_name[0]]?.[0];
      } else {
        invoice = response.data[payload.client_name]?.[0];
      }

      if (!invoice) {
        for (const key in response.data) {
          const invoices = response.data[key];
          if (Array.isArray(invoices)) {
            invoice = invoices.find(
              (inv: Invoice) =>
                inv.id === payload.invoice_id || inv.invoice_number === payload.invoice_id
            );
            if (invoice) break;
          }
        }
      }

      if (!invoice) {
        throw new Error(`Invoice with ID ${payload.invoice_id} not found`);
      }

      console.log('Extracted invoice:', invoice);
      return invoice as Invoice;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch invoice');
    }
  }
);

export const fetchClientAndOrgInfo = createAsyncThunk<
  { organization: Organization; client: Client },
  ClientAndOrgPayload,
  { rejectValue: string }
>(
  'invoice/fetchClientAndOrgInfo',
  async ({ group_id, service_name_id, client_name }, { rejectWithValue }) => {
    try {
      const payload = {
        group_id,
        service_name_id,
        client_name
      };
      const response = await axiosInstance.post(
        '/ndis/corporate-services/get_client_and_org_info',
        payload
      );
      const { organisation_info, client_info } = response.data;
      return {
        organization: organisation_info,
        client: {
          ...client_info,
          client_name: client_info.client_full_name
        }
      };
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch client and organization info'
      );
    }
  }
);

export const fetchWorkloadInvoice = createAsyncThunk(
  'invoice/fetchWorkloadInvoice',
  async (payload: InvoicePayload & { invoice_id: string }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post('/get/invoiceData', payload);
      console.log('fetchWorkloadInvoice response:', response.data);

      let invoice: Invoice | undefined;

      if (Array.isArray(payload.client_name)) {
        invoice = response.data[payload.client_name[0]]?.[0];
      } else {
        invoice = response.data[payload.client_name]?.[0];
      }

      if (!invoice) {
        for (const key in response.data) {
          const invoices = response.data[key];
          if (Array.isArray(invoices)) {
            invoice = invoices.find(
              (inv: Invoice) =>
                inv.id === payload.invoice_id || inv.invoice_number === payload.invoice_id
            );
            if (invoice) break;
          }
        }
      }

      if (!invoice) {
        throw new Error(`Invoice with ID ${payload.invoice_id} not found`);
      }

      console.log('Extracted workload invoice:', invoice);
      return invoice as Invoice;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch workload invoice');
    }
  }
);

export const editInvoice = createAsyncThunk(
  'invoice/editInvoice',
  async (
    payload: InvoicePayload & { invoice_id: string; updated_invoice: Partial<Invoice> },
    { rejectWithValue }
  ) => {
    try {
      const response = await axiosInstance.post('/edit/invoiceData', payload);
      console.log('editInvoice response:', response.data);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to edit invoice');
    }
  }
);

export const deleteInvoice = createAsyncThunk(
  'invoice/deleteInvoice',
  async (payload: InvoicePayload & { invoice_id: string }, { rejectWithValue }) => {
    try {
      await axiosInstance.post('/delete/invoiceData', payload);
      console.log('deleteInvoice success for invoice_id:', payload.invoice_id);
      return payload.invoice_id;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete invoice');
    }
  }
);

export const sendInvoice = createAsyncThunk(
  'invoice/sendInvoice',
  async (payload: SendInvoicePayload, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post('/ndis/finance/send-invoice', payload);
      console.log('sendInvoice response:', response.data);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to send invoice');
    }
  }
);

const invoiceSlice = createSlice({
  name: 'invoice',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearSendError: (state) => {
      state.sendError = null;
    },
    clearInvoices: (state) => {
      state.invoices = [];
      state.current_invoice = null;
      state.organization = null;
      state.client = null;
      state.error = null;
      state.sendError = null;
    },
    clearGeneratedInvoices: (state) => {
      state.generatedInvoices = [];
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchInvoice.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchInvoice.fulfilled, (state, action) => {
        state.loading = false;
        state.invoices = action.payload;
      })
      .addCase(fetchInvoice.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchSingleInvoice.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSingleInvoice.fulfilled, (state, action) => {
        state.loading = false;
        state.current_invoice = action.payload;
      })
      .addCase(fetchSingleInvoice.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchClientAndOrgInfo.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchClientAndOrgInfo.fulfilled, (state, action) => {
        state.loading = false;
        state.organization = action.payload.organization;
        state.client = action.payload.client;
      })
      .addCase(fetchClientAndOrgInfo.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchWorkloadInvoice.pending, (state) => {
        state.error = null;
      })
      .addCase(fetchWorkloadInvoice.fulfilled, (state, action) => {
        state.current_invoice = action.payload;
      })
      .addCase(fetchWorkloadInvoice.rejected, (state, action) => {
        state.error = action.payload as string;
      })
      .addCase(generateInvoice.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(generateInvoice.fulfilled, (state, action) => {
        state.loading = false;
        state.generatedInvoices = action.payload;
      })
      .addCase(generateInvoice.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(editInvoice.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(editInvoice.fulfilled, (state, action) => {
        state.loading = false;
        state.invoices = state.invoices.map((invoice) =>
          invoice.id === action.payload.id ? action.payload : invoice
        );
        state.generatedInvoices = state.generatedInvoices.map((invoice) =>
          invoice.id === action.payload.id ? action.payload : invoice
        );
        state.current_invoice = action.payload;
      })
      .addCase(editInvoice.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(deleteInvoice.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteInvoice.fulfilled, (state, action) => {
        state.loading = false;
        state.invoices = state.invoices.filter(
          (invoice) => invoice.id !== action.payload && invoice.invoice_number !== action.payload
        );
        state.generatedInvoices = state.generatedInvoices.filter(
          (invoice) => invoice.id !== action.payload && invoice.invoice_number !== action.payload
        );
      })
      .addCase(deleteInvoice.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(sendInvoice.pending, (state) => {
        state.sending = true;
        state.sendError = null;
      })
      .addCase(sendInvoice.fulfilled, (state, action) => {
        state.sending = false;
      })
      .addCase(sendInvoice.rejected, (state, action) => {
        state.sending = false;
        state.sendError = action.payload as string;
      });
  }
});

export const { clearError, clearSendError, clearInvoices, clearGeneratedInvoices } =
  invoiceSlice.actions;
export default invoiceSlice.reducer;
